import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, AtSign, UserPlus, Check } from "lucide-react";

const KIND_ICON = {
  mention: AtSign,
  invite_accepted: UserPlus,
};

const formatRel = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString();
};

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = items.filter((n) => !n.read).length;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read");
      await load();
    } catch {}
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full h-9 w-9" data-testid="notifications-bell">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold grid place-items-center" data-testid="notifications-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="notifications-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <span className="font-medium text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-amber-700 hover:underline" data-testid="notifications-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <ul className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-stone-500">You're all caught up.</li>
          ) : items.map((n) => {
            const Icon = KIND_ICON[n.kind] || Bell;
            return (
              <li key={n.id} className={`border-b border-stone-100 last:border-b-0 ${!n.read ? "bg-amber-50/40" : ""}`} data-testid={`notification-${n.id}`}>
                <Link to={n.link || "#"} onClick={() => setOpen(false)} className="flex gap-3 px-4 py-3 hover:bg-stone-50">
                  <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${!n.read ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-xs text-stone-500 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400 mt-1">{formatRel(n.created_at)}</div>
                  </div>
                  {!n.read && <Check className="w-3 h-3 text-amber-600 shrink-0" />}
                </Link>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
